import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { onAuthStateChanged } from 'firebase/auth';
import { v4 as uuidv4 } from 'uuid';
import { auth } from '../../firebase';
import axios from '../../utils/axios';
import config from '../../config/env';
import { createAuthedThunk } from '../authedThunk';

const mutationInitialState = {
  processing: null,
  done: null,
  failed: null,
  error: null,
  errorText: null,
};
const fetchDataInitialState = {
  fetching: null,
  fetchingSuccessful: null,
  fetchingFailed: null,
  error: null,
  errorText: null,
  data: null,
};

const initialState = {
  logError: { ...mutationInitialState },
  trackEvent: { ...mutationInitialState },
  tempState: { ...fetchDataInitialState },
  saveTempState: { ...mutationInitialState },
  plans: { ...fetchDataInitialState },
  privacyTos: { ...fetchDataInitialState },
  unsubscribe: { ...mutationInitialState },
  sendUpgradeRequest: { ...mutationInitialState },
  updateNotificationPreferences: { ...mutationInitialState },
  userPreferences: { ...fetchDataInitialState },
  allPreferences: { ...fetchDataInitialState },
  playbookHistory: { ...fetchDataInitialState },
  emailByToken: { ...fetchDataInitialState },
  validateDeepLink: { ...fetchDataInitialState },
  validateFeaturePreviewLink: { ...fetchDataInitialState },
};

// Shared auth wrapper - see `redux/authedThunk`. Was a byte-identical
// local copy in each of eleven slices.
const thunk = createAuthedThunk;

export const logErrorThunk = createAsyncThunk(
  'misc/logError',
  async (errorDetails, { rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          try {
            const token = user ? await user.getIdToken() : null;
            const r = await axios.post('/log', errorDetails, {
              params: { 'request-id': uuidv4() },
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (r.status === 200) resolve(r.data);
            else reject(rejectWithValue('Failed to log error'));
          } catch (e) {
            reject(rejectWithValue(e?.response?.data || e.message));
          }
        },
        (error) => reject(rejectWithValue(error.message)),
      );
    });
  },
);

/**
 * Session-lifetime cache of the client's public IP.
 *
 * `trackEventThunk` fires on every tracked interaction and used to make a
 * third-party round trip to ipify before each one. The address does not change
 * within a session, so the in-flight promise is shared and the result is kept.
 * A failed lookup clears the cache so the next event can retry.
 *
 * @type {Promise<string|null>|null}
 */
let publicIpPromise = null;

/**
 * @returns {Promise<string|null>} the cached public IP, or null if unavailable
 */
const getPublicIp = () => {
  if (!publicIpPromise) {
    publicIpPromise = axios
      .get('https://api.ipify.org?format=json')
      .then((res) => res?.data?.ip || null)
      .catch(() => {
        publicIpPromise = null;
        return null;
      });
  }
  return publicIpPromise;
};

export const trackEventThunk = createAsyncThunk(
  'misc/trackEvent',
  async (eventDto, { rejectWithValue }) => {
    try {
      const ip = await getPublicIp();
      let r;
      if (config.isLocal) {
        r = { status: 200, data: null };
      } else {
        r = await axios.post(
          '/us/events',
          { ...eventDto, ip },
          { params: { 'request-id': uuidv4() } },
        );
      }
      if (r.status === 200) return r.data;
      return rejectWithValue('Failed to track event');
    } catch (e) {
      return rejectWithValue(e?.response?.data || e.message);
    }
  },
);

export const fetchTempState = thunk('misc/fetchTempState', async (token) => {
  const r = await axios.get('/us/temp-state', {
    params: { 'request-id': uuidv4() },
    headers: { Authorization: `Bearer ${token}` },
  });
  if (r.status === 200) return r.data;
  throw new Error('Failed to fetch temp state');
});

export const saveTempStateThunk = thunk(
  'misc/saveTempState',
  async (token, payload) => {
    const r = await axios.post('/us/temp-state', payload, {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to save temp state');
  },
);

export const fetchPlans = thunk('misc/fetchPlans', async (token, domain) => {
  const r = await axios.post(
    '/us/plans',
    { domain },
    {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    },
  );
  if (r.status === 200) return r.data;
  throw new Error('Failed to fetch plans');
});

export const fetchPrivacyTos = createAsyncThunk(
  'misc/fetchPrivacyTos',
  async (type, { rejectWithValue }) => {
    try {
      const r = await axios.get('/us/tos-and-privacy', {
        params: { 'request-id': uuidv4(), type },
      });
      if (r.status === 200) return r.data;
      return rejectWithValue('Failed to get privacy/tos');
    } catch (e) {
      return rejectWithValue(e?.response?.data || e.message);
    }
  },
);

export const unsubscribeFromNotifications = createAsyncThunk(
  'misc/unsubscribe',
  async ({ email, tokens, payload }, { rejectWithValue }) => {
    try {
      const r = await axios.post('/nf/user-preferences/unsubscribe', payload, {
        params: {
          'request-id': uuidv4(),
          email,
          tokens: encodeURIComponent(tokens),
        },
      });
      if (r.status === 200) return r.data?.data;
      return rejectWithValue({ text: r.data?.text, is400: false });
    } catch (e) {
      if (e?.response?.status === 400 && e?.response?.data?.status === 400) {
        return rejectWithValue({ text: e?.response?.data?.text, is400: true });
      }
      return rejectWithValue({ text: null, is400: false });
    }
  },
);

export const sendUpgradeRequestThunk = createAsyncThunk(
  'misc/sendUpgradeRequest',
  async ({ type, planId }, { rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          try {
            const token = user ? await user.getIdToken() : null;
            const r = await axios.post('/us/contact-sales/v2', null, {
              params: { 'request-id': uuidv4(), type, planId },
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (r.status === 200) resolve(r.data);
            else reject(rejectWithValue({ text: r.data?.text, is400: false }));
          } catch (e) {
            if (
              e?.response?.status === 400 &&
              e?.response?.data?.status === 400
            ) {
              reject(
                rejectWithValue({ text: e?.response?.data?.text, is400: true }),
              );
            } else {
              reject(rejectWithValue({ text: null, is400: false }));
            }
          }
        },
        (error) => reject(rejectWithValue({ text: null, is400: false })),
      );
    });
  },
);

export const fetchUserPreferencesThunk = thunk(
  'misc/fetchUserPreferences',
  async (token) => {
    const r = await axios.get('/nf/user-preferences', {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to fetch user preferences');
  },
);

export const updateNotificationPreferencesThunk = thunk(
  'misc/updateNotificationPreferences',
  async (token, payload) => {
    const r = await axios.post('/nf/user-preferences', payload, {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to update notification preferences');
  },
);

export const fetchAllPreferencesThunk = thunk(
  'misc/fetchAllPreferences',
  async (token) => {
    const r = await axios.get('/nf/preferences/all', {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to fetch all preferences');
  },
);

export const fetchPlaybookHistory = thunk(
  'misc/fetchPlaybookHistory',
  async (token, teamId) => {
    const r = await axios.get('/us/profiles/playbooks', {
      params: { 'request-id': uuidv4(), size: 50, last_seen: null, teamId },
      headers: { Authorization: `Bearer ${token}`, 'x-api-version': 'v2' },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to fetch playbook history');
  },
  { dedupeKey: (teamId) => `playbookHistory:${teamId || 'none'}` },
);

export const fetchEmailByToken = createAsyncThunk(
  'misc/fetchEmailByToken',
  async (token, { rejectWithValue }) => {
    try {
      const r = await axios.get(
        `/nf/user-preferences/email-by-token/${token}`,
        { params: { 'request-id': uuidv4() } },
      );
      if (r.status === 200) return r.data?.data;
      return rejectWithValue('Failed to get email by token');
    } catch (e) {
      return rejectWithValue(e?.response?.data || e.message);
    }
  },
);

export const validateDeepLinkThunk = thunk(
  'misc/validateDeepLink',
  async (token, rtid) => {
    const r = await axios.get(`/us/qai/interaction/token/${rtid}`, {
      params: { requestId: uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to validate deep link');
  },
);

export const validateFeaturePreviewLinkThunk = createAsyncThunk(
  'misc/validateFeaturePreviewLink',
  async (fptoken, { rejectWithValue }) => {
    try {
      const r = await axios.get(`/us/qai/feature-preview/token/${fptoken}`, {
        params: { requestId: uuidv4() },
      });
      if (r.status === 200) return r.data;
      return rejectWithValue('Failed to validate feature preview link');
    } catch (e) {
      return rejectWithValue(e?.response?.data || e.message);
    }
  },
);

const addM = (b, t, k) =>
  b
    .addCase(t.pending, (s) => {
      s[k].processing = true;
      s[k].done = false;
      s[k].failed = false;
    })
    .addCase(t.fulfilled, (s) => {
      s[k].processing = false;
      s[k].done = true;
      s[k].failed = false;
    })
    .addCase(t.rejected, (s, a) => {
      s[k].processing = false;
      s[k].done = false;
      s[k].failed = true;
      s[k].error = a.payload;
    });

const addF = (b, t, k) =>
  b
    .addCase(t.pending, (s) => {
      s[k].fetching = true;
      s[k].fetchingSuccessful = false;
      s[k].fetchingFailed = false;
    })
    .addCase(t.fulfilled, (s, a) => {
      s[k].fetching = false;
      s[k].fetchingSuccessful = true;
      s[k].fetchingFailed = false;
      s[k].data = a.payload;
    })
    .addCase(t.rejected, (s) => {
      s[k].fetching = false;
      s[k].fetchingSuccessful = false;
      s[k].fetchingFailed = true;
    });

const miscSlice = createSlice({
  name: 'misc',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    addM(builder, logErrorThunk, 'logError');
    addM(builder, trackEventThunk, 'trackEvent');
    addF(builder, fetchTempState, 'tempState');
    addM(builder, saveTempStateThunk, 'saveTempState');
    addF(builder, fetchPlans, 'plans');
    addF(builder, fetchPrivacyTos, 'privacyTos');
    addM(builder, unsubscribeFromNotifications, 'unsubscribe');
    addM(builder, sendUpgradeRequestThunk, 'sendUpgradeRequest');
    addM(
      builder,
      updateNotificationPreferencesThunk,
      'updateNotificationPreferences',
    );
    addF(builder, fetchUserPreferencesThunk, 'userPreferences');
    addF(builder, fetchAllPreferencesThunk, 'allPreferences');
    addF(builder, fetchPlaybookHistory, 'playbookHistory');
    addF(builder, fetchEmailByToken, 'emailByToken');
    addF(builder, validateDeepLinkThunk, 'validateDeepLink');
    addF(
      builder,
      validateFeaturePreviewLinkThunk,
      'validateFeaturePreviewLink',
    );
  },
});

export default miscSlice.reducer;
